import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from "@asm/ui";
import { Layers, Scan, Zap, Globe, Gauge, Infinity, Save, Loader2, Package, Check, Webhook } from "lucide-react";
import { adminApi, TierConfig } from "../lib/api";

const tierStyles: Record<string, { color: string; accent: string; textColor: string }> = {
  free: {
    color: "border-muted bg-muted/20",
    accent: "bg-muted/30",
    textColor: "text-muted-foreground",
  },
  starter: {
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "bg-emerald-500/10",
    textColor: "text-emerald-400",
  },
  professional: {
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "bg-amber-500/10",
    textColor: "text-amber-400",
  },
  enterprise: {
    color: "border-red-500/30 bg-red-500/5",
    accent: "bg-red-500/10",
    textColor: "text-red-400",
  },
};

function LimitInput({
  icon: Icon,
  label,
  value,
  onChange,
  placeholder,
  accentColor,
}: {
  icon: React.ElementType;
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  accentColor: string;
}) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <div className={`p-1 rounded ${accentColor}`}>
          <Icon className="h-3 w-3" />
        </div>
        {label}
      </Label>
      <Input
        type="number"
        value={value ?? ""}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === "" ? undefined : parseInt(val, 10));
        }}
        placeholder={placeholder}
        className="font-mono"
      />
    </div>
  );
}

function FeatureToggle({
  icon: Icon,
  label,
  checked,
  onChange,
  accentColor,
}: {
  icon: React.ElementType;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  accentColor: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <div className={`p-1 rounded ${accentColor}`}>
          <Icon className="h-3 w-3" />
        </div>
        {label}
      </Label>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
          checked ? "bg-emerald-500" : "bg-muted"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 flex items-center justify-center ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        >
          {checked && <Check className="h-2.5 w-2.5 text-emerald-500" />}
        </span>
      </button>
    </div>
  );
}

interface TierEdits {
  [tier: string]: Partial<TierConfig>;
}

export default function Tiers() {
  const [tiers, setTiers] = useState<TierConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [edits, setEdits] = useState<TierEdits>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.listTiers();
      setTiers(response.tiers);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tiers");
    } finally {
      setLoading(false);
    }
  };

  const updateEdit = (tierName: string, field: keyof TierConfig, value: unknown) => {
    setEdits((prev) => ({
      ...prev,
      [tierName]: {
        ...prev[tierName],
        [field]: value,
      },
    }));
  };

  const getEffectiveValue = <K extends keyof TierConfig>(
    tier: TierConfig,
    field: K
  ): TierConfig[K] => {
    const edit = edits[tier.tier];
    if (edit && field in edit) {
      return edit[field] as TierConfig[K];
    }
    return tier[field];
  };

  const hasChanges = (tierName: string) => {
    return edits[tierName] && Object.keys(edits[tierName]).length > 0;
  };

  const saveTier = async (tierName: string) => {
    const tierEdits = edits[tierName];
    if (!tierEdits || Object.keys(tierEdits).length === 0) return;

    try {
      setSaving(tierName);
      await adminApi.updateTier(tierName, tierEdits);
      // Reload tiers to get fresh data
      await loadTiers();
      // Clear edits for this tier
      setEdits((prev) => {
        const { [tierName]: _, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save tier");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <Layers className="h-8 w-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tier Configuration</h1>
            <p className="text-muted-foreground">Manage subscription tier limits and features</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Tier Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {tiers.map((tier, index) => {
          const style = tierStyles[tier.tier] || tierStyles.free;
          const isUnlimited = !tier.monthly_scan_quota;

          return (
            <Card
              key={tier.tier}
              className={`animate-fade-in-up stagger-${index + 1} ${style.color} hover:shadow-lg transition-shadow`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-6 w-1 rounded-full ${style.textColor.replace("text-", "bg-")}`} />
                    <div>
                      <CardTitle className={`capitalize ${style.textColor}`}>{tier.tier}</CardTitle>
                      <CardDescription>
                        Configure limits for {tier.tier} tier
                      </CardDescription>
                    </div>
                  </div>
                  {isUnlimited && (
                    <div className={`p-1.5 rounded-lg ${style.accent}`}>
                      <Infinity className={`h-4 w-4 ${style.textColor}`} />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Numeric Limits */}
                <div className="grid grid-cols-2 gap-4">
                  <LimitInput
                    icon={Scan}
                    label="Scans/Month"
                    value={getEffectiveValue(tier, "monthly_scan_quota")}
                    onChange={(v) => updateEdit(tier.tier, "monthly_scan_quota", v)}
                    placeholder="Unlimited"
                    accentColor={style.accent}
                  />
                  <LimitInput
                    icon={Zap}
                    label="Concurrent Scans"
                    value={getEffectiveValue(tier, "max_concurrent_scans")}
                    onChange={(v) => updateEdit(tier.tier, "max_concurrent_scans", v)}
                    accentColor={style.accent}
                  />
                  <LimitInput
                    icon={Globe}
                    label="Max Domains"
                    value={getEffectiveValue(tier, "max_domains")}
                    onChange={(v) => updateEdit(tier.tier, "max_domains", v)}
                    placeholder="Unlimited"
                    accentColor={style.accent}
                  />
                  <LimitInput
                    icon={Gauge}
                    label="Rate Limit (RPM)"
                    value={getEffectiveValue(tier, "rate_limit_rpm")}
                    onChange={(v) => updateEdit(tier.tier, "rate_limit_rpm", v)}
                    accentColor={style.accent}
                  />
                </div>

                {/* Feature Toggles */}
                <div className="space-y-3 pt-2 border-t border-border/50">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                    Features
                  </p>
                  <FeatureToggle
                    icon={Package}
                    label="Asset Inventory"
                    checked={getEffectiveValue(tier, "asset_inventory_enabled")}
                    onChange={(v) => updateEdit(tier.tier, "asset_inventory_enabled", v)}
                    accentColor={style.accent}
                  />
                  <FeatureToggle
                    icon={Webhook}
                    label="Webhooks"
                    checked={getEffectiveValue(tier, "webhooks_enabled")}
                    onChange={(v) => updateEdit(tier.tier, "webhooks_enabled", v)}
                    accentColor={style.accent}
                  />
                  {getEffectiveValue(tier, "webhooks_enabled") && (
                    <div className="pl-7">
                      <LimitInput
                        icon={Webhook}
                        label="Max Webhooks"
                        value={getEffectiveValue(tier, "max_webhooks")}
                        onChange={(v) => updateEdit(tier.tier, "max_webhooks", v)}
                        placeholder="Unlimited"
                        accentColor={style.accent}
                      />
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className={`w-full hover:${style.accent} hover:${style.textColor} transition-colors`}
                  onClick={() => saveTier(tier.tier)}
                  disabled={!hasChanges(tier.tier) || saving === tier.tier}
                >
                  {saving === tier.tier ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {hasChanges(tier.tier) ? "Save Changes" : "No Changes"}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
